<script lang="ts">
	import type { IconPosition } from '$lib/components/atoms/iconlabel/types';
	import type { IconName } from '$lib/components/icons/types';
	import type { ChipImageSize, ChipScheme, ChipSchemeClass, ChipStyle } from './types';
	import IconLabel from '$lib/components/atoms/iconlabel/IconLabel.svelte';
	import Image from '$lib/components/atoms/image/Image.svelte';
	import { getStyle } from '$lib/utils/getStyle';

	interface Props {
		selected?: boolean;
		text?: string;
		scheme?: ChipScheme;
		icon?: IconName;
		iconPosition?: IconPosition;
		hideTextOnMobile?: boolean;
		isSquare?: boolean;
		ariaLabel?: string;
		imgUrl?: string;
		selectedColorStyles?: ChipStyle;
		imgSize?: ChipImageSize;
		onSelectedChange?: (selectedChange: boolean) => void;
	}

	let {
		text,
		scheme,
		icon,
		ariaLabel,
		imgUrl,
		selectedColorStyles,
		iconPosition = 'left',
		imgSize = 'md',
		hideTextOnMobile = false,
		isSquare = false,
		selected = $bindable(false),
		onSelectedChange,
	}: Props = $props();

	const selectedClasses: Record<ChipScheme, ChipSchemeClass	> = {
		primary: 'bg-coloredneutral text-neutral-weakest hover:bg-coloredneutral-soft',
		hotel: 'bg-hotel text-neutral-weakest hover:bg-hotel-soft',
		restaurant: 'bg-restaurant text-neutral-weakest hover:bg-restaurant-soft',
		tourism: 'bg-tourism text-neutral-weakest hover:bg-tourism-soft',
		energy: 'bg-fuel text-neutral-weakest hover:bg-fuel-soft',
		parking: 'bg-parking text-neutral-weakest hover:bg-parking-soft',
		electric: 'bg-electric text-neutral-weakest hover:bg-electric-soft',
		rest: 'bg-rest-stop text-neutral-weakest hover:bg-rest-stop-soft',
	};

	const unselectedClasses = 'bg-neutral-soft hover:bg-coloredneutral-weak text-coloredneutral';

	const chipImageClass: Record<ChipImageSize, string> = {
		md: 'size-4',
		lg: 'size-5',
	};

	const hasIconOrImageOnly = $derived((icon || imgUrl) && !text);
	const squareClasses = $derived(isSquare ? 'rd-2' : 'rounded-full');
	const iconOrImageClasses = $derived(hasIconOrImageOnly && imgSize === 'lg' ? 'p-2' : 'p-2.5');

	const style = $derived.by(() => {
		if (selected) {
			return selectedColorStyles ? getStyle<ChipStyle>(selectedColorStyles) : '';
		}
	});

	const schemeClasses = $derived.by(() => {
		if (scheme) {
			return selected
				? selectedClasses[scheme]
				: unselectedClasses;
		}
		return unselectedClasses;
	});
</script>

<button
	type="button"
	role="option"
	title={text ? '' : ariaLabel}
	aria-selected={selected}
	aria-label={ariaLabel}
	class={['whitespace-nowrap flex justify-center items-center text-md gap-1 b-0 cursor-pointer', squareClasses, iconOrImageClasses, schemeClasses]}
	class:with-style={style}
	class:aspect-ratio-1={hasIconOrImageOnly}
	{...style ? { style } : {}}
	onclick={() => {
		selected = !selected;
		onSelectedChange?.(selected);
	}}
>
	{#if imgUrl}
		<Image src={imgUrl} alt={text || ''} class={chipImageClass[imgSize]} />
		{#if text}
			<span class:lt-md:sr-only={hideTextOnMobile}>{text}</span>
		{/if}
	{:else}
		<IconLabel
			{icon}
			label={text}
			hideLabelOnMobile={hideTextOnMobile}
			{iconPosition}
			tag="span"
			iconTag="span"
		/>
	{/if}
</button>

<style>
	.with-style {
		background-color: var(--bg-color);
		color: var(--color);
		&:hover {
			background-color: var(--hover-bg-color);
			color: var(--color);
		}
	}
</style>
